<template>
  <div class="-mx-2 -mt-2 mb-4 flex justify-start rounded-lg border border-slate-200 px-4 pt-4 pb-4"
       :class="containerClasses">
    <div class="flex items-center justify-center rounded bg-slate-100 min-w-[3rem]"
         :class="small ? 'h-6 w-6' : 'h-12 w-12'"
         :style="backgroundStyles">
      <Component :is="icon" :class="small ? 'h-3 w-3' : 'h-5 w-5'"/>
    </div>
    <div class="text-sm text-slate-900">
      <p v-if="typeof item === 'string'" class="font-bold">{{ item }}</p>
      <template v-else>
        <div v-if="item.primary || item.type" class="font-bold -mt-0.5">
          {{ completionMessage }}
          <span v-if="item.primary">{{ item.primary }}</span>
          <span v-if="item.primary && item.type"> | </span>
          <span v-if="item.type">{{ item.type }}</span>
        </div>
        <div v-if="item.title" :class="!(item.primary || item.type) && 'font-bold'">{{ item.title }}</div>
        <div v-if="item.rank" class="text-slate-400">
          <span class="font-medium">Self Rated {{ item.rank }}/10</span> <span
          class="italic opacity-80">– "{{ rankMessage }}"</span></div>
        <div v-if="item.subtitle" class="text-slate-400">{{ item.subtitle }}</div>
        <div v-if="item.start || item.end" class="text-slate-400">{{ tenure }}</div>
        <div v-if="item.description" class="mt-4 text-slate-600">{{ item.description }}</div>
        <a v-if="item.link" :href="item.link" target="_blank"
           class="mt-4 flex items-center text-primary-600 hover:underline">
          View
          <ArrowTopRightOnSquareIcon class="ml-1 h-4 w-4"/>
        </a>
      </template>
    </div>
    <div class="ml-auto flex flex-col items-center md:flex-row gap-3">
      <HoverTooltip tip="Move Item Up" placement="left">
        <FormButton @click="updateOrder(-1)" color="secondary" size="xs" :disabled="index===0">
          <ChevronUpIcon class="h-4 w-4"/>
        </FormButton>
      </HoverTooltip>
      <HoverTooltip tip="Move Item Down" placement="left">
        <FormButton @click="updateOrder(1)" color="secondary" size="xs" :disabled="index === itemCount - 1">
          <ChevronDownIcon class="h-4 w-4"/>
        </FormButton>
      </HoverTooltip>
      <HoverTooltip tip="Edit Item" placement="left">
        <FormButton @click="setIsEditing(true)" color="green" size="xs">
          <PencilSquareIcon class="h-4 w-4"/>
        </FormButton>
      </HoverTooltip>
      <HoverTooltip tip="Remove Item" placement="left">
        <FormButton color="red" size="xs" @click="setConfirmRemove(true)">
          <TrashIcon class="h-4 w-4"/>
        </FormButton>
      </HoverTooltip>
    </div>
    <EditableFormDetailedListItemModal v-model:open="isEditing" :item="item" :allows="allows"
                                       @update:item="updateItem"/>
    <ModalConfirm v-model:open="confirmRemove"
                  title="Are you sure you want to remove this item?"
                  level="danger"
                  description="There is no undo – You'll have to re-add it if you want it again."
                  proceed-button-text="Remove Entry"
                  class="z-max"
                  @confirm="deleteItem"/>
  </div>
</template>

<script setup>
import FormButton from "./FormButton.vue";
import HoverTooltip from "./HoverTooltip.vue";
import EditableFormDetailedListItemModal from "./EditableFormDetailedListItemModal.vue";
import ModalConfirm from "./ModalConfirm.vue";
import {getColorAtIndex} from "../helpers/OpportunityFunctions.js";
import {getItemCompletionMessage, getRankMessage} from "../helpers/CandidateFunctions.js";
import {formatTenure} from "../helpers/FormatDates.js";
import {ArrowTopRightOnSquareIcon} from "@heroicons/vue/24/outline/index.js";
import {ChevronDownIcon, ChevronUpIcon, PencilSquareIcon, TrashIcon} from "@heroicons/vue/24/solid/index.js";
import {computed, onMounted, ref} from "vue";

const emits = defineEmits(["update:item", "update:order", "delete:item"]);
const props = defineProps({
  item: [Object, String],
  index: Number,
  small: Boolean,
  icon: {
    type: [Object, Function],
    required: true,
  },
  itemCount: Number,
  allows: Object,
});

const isEditing = ref(false);
const confirmRemove = ref(false);

const setIsEditing = (value) => isEditing.value = value;

const updateItem = (value) => {
  const item = typeof props.item === "string" ? value : {...props.item, ...value};
  emits("update:item", item);
};

const updateOrder = (direction) => {
  if (direction === 0) return;
  if (direction === -1 && props.index === 0) return;
  if (direction === 1 && props.index === props.itemCount - 1) return;
  toast.info("Item moved " + (direction === -1 ? "up" : "down"));
  emits("update:order", direction);
};

const setConfirmRemove = (value) => confirmRemove.value = value;

const deleteItem = () => emits("delete:item");

const containerClasses = computed(() => {
  const classes = [];

  if (props.index !== 0) classes.push("pt-6");

  if (props.index !== props.itemCount - 1) classes.push("pb-6");

  if (typeof props.item === "string") classes.push("items-center");
  else classes.push("items-start gap-4 md:gap-6");

  if (props.small) classes.push("gap-2 md:gap-3");
  else classes.push("items-start gap-4 md:gap-6");

  if (typeof props.item === "string" || !props.item?.description) classes.push("items-center");

  return classes.join(" ");
});

const completionMessage = computed(() => getItemCompletionMessage(props.item?.completion, props.item?.primary, props.item?.type));
const tenure = computed(() => formatTenure(props.item?.start, props.item?.end));
const rankMessage = computed(() => getRankMessage(props.item.rank));

const backgroundStyles = computed(() => {
  return {
    background: getColorAtIndex(props.index, props.itemCount, 0.15),
    color: getColorAtIndex(props.index, props.itemCount, 1),
  };
});

onMounted(() => {
  if (!props.item?.isNew) return;
  updateItem({...props.item, isNew: undefined});
  setIsEditing(true);
});
</script>
